/**
 * Shared runtime context passed to every submodule.
 *
 * Centralizing config validation + default application here means each
 * module operates on a guaranteed-complete ResolvedConfig and can reach
 * the shared supabase client, logger, and in-memory player cache.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { VibecadeError, VibecadeErrorCode, type Player, type VibecadeConfig } from '../types';

/** Production platform endpoint (canonical Supabase project host). */
export const DEFAULT_API_URL = 'https://kyhrmknwykrcdxkbrkiz.supabase.co';

/**
 * Production anon key. Baked in at publish time so consumers can do
 * `new Vibecade({ gameId, apiKey })` without also passing `anonKey`.
 *
 * Anon keys are **public by design** — access control lives in RLS + JWT,
 * not in the key itself. Committing this is the Supabase-recommended
 * pattern and matches the value already shipping in the launcher's
 * `.env.example`.
 *
 * Local/staging consumers still override via the `anonKey` field in
 * `VibecadeConfig` when pointing at a non-prod Supabase project.
 */
export const DEFAULT_ANON_KEY =
  'sb_publishable_v9FIIJV5nD53vILom8ogqg_d3Lf2LDF';

export interface ResolvedConfig {
  gameId: string;
  apiKey: string;
  apiUrl: string;
  anonKey: string;
  debug: boolean;
  autoHostMessages: boolean;
  /** Identity slot; defaults to gameId. See VibecadeConfig.authScope. */
  authScope: string;
}

export interface Logger {
  debug: (...args: unknown[]) => void;
}

export interface SdkContext {
  config: ResolvedConfig;
  /**
   * Owns the session: sign-in, setSession, onAuthStateChange. Only `.auth` is
   * used on it. Reads and writes go through `db`.
   */
  sb: SupabaseClient;
  /**
   * Every PostgREST read and write (`.from`, `.rpc`). Its token comes from
   * `accessToken`, not from auth-js. See createDataClient for why.
   */
  db: SupabaseClient;
  /**
   * The bearer token the SDK's own requests carry, or null for none. Edge
   * Function calls and `db` both read it. See AuthModule.requestToken.
   */
  accessToken: () => Promise<string | null>;
  logger: Logger;
  /** Mutable cell holding the current player; set on init, mutated by identify. */
  player: { current: Player | null };
}

export function createLogger(debug: boolean): Logger {
  return {
    debug: debug
      ? (...args) => {
          // eslint-disable-next-line no-console
          console.debug('[vibecade]', ...args);
        }
      : () => {},
  };
}

/**
 * Validate config input and apply defaults. Throws VibecadeError (code
 * INVALID_CONFIG) on the first invalid field.
 *
 * `apiUrl`, when given, must parse as an absolute http(s) URL; the
 * effective value is then normalized by stripping trailing slashes so
 * the Edge Function URL join in `edgeFunctions.ts` never produces a
 * double slash. `anonKey` is required whenever the normalized `apiUrl`
 * differs from `DEFAULT_API_URL` — the baked-in anon key only matches
 * the production Supabase project, so pairing it with a different
 * host would silently authenticate against the wrong backend.
 * Passing the production URL explicitly, as written (with or without
 * a trailing slash), stays exempt from that requirement; other
 * spellings of the same host (case variants, an explicit :443) are
 * treated as overrides and need an explicit anonKey.
 */
export function resolveConfig(input: VibecadeConfig): ResolvedConfig {
  if (!input || typeof input !== 'object') {
    throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'Config object is required');
  }
  if (typeof input.gameId !== 'string' || !input.gameId) {
    throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'config.gameId is required');
  }
  if (typeof input.apiKey !== 'string' || !input.apiKey) {
    throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'config.apiKey is required');
  }
  if (input.apiUrl !== undefined && (typeof input.apiUrl !== 'string' || !input.apiUrl)) {
    throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'config.apiUrl must be a non-empty string');
  }
  if (input.apiUrl !== undefined) {
    let parsed: URL;
    try {
      parsed = new URL(input.apiUrl);
    } catch {
      throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'config.apiUrl must be a valid absolute URL');
    }
    // WHATWG URL happily parses 'localhost:5000' with scheme
    // 'localhost:' — the single most likely local-dev typo (a missing
    // http://) would otherwise pass this guard and die later as a
    // misleading NETWORK_ERROR inside fetch.
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'config.apiUrl must be an absolute http(s) URL',
      );
    }
  }
  if (input.anonKey !== undefined && (typeof input.anonKey !== 'string' || !input.anonKey)) {
    throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'config.anonKey must be a non-empty string');
  }
  if (input.authScope !== undefined) {
    if (typeof input.authScope !== 'string' || !input.authScope) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'config.authScope must be a non-empty string',
      );
    }
    // This becomes part of a localStorage key and of a Web Locks name.
    // Neither has an escaping story, and a scope carrying a separator or a
    // space would silently collide with, or fail to match, another client
    // that meant the same thing — which presents as two anonymous players
    // where there should be one, the exact failure authScope exists to
    // prevent. Same character set game ids already use.
    if (!/^[A-Za-z0-9._-]+$/.test(input.authScope)) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'config.authScope may contain only letters, digits, dot, underscore and hyphen',
      );
    }
  }
  const apiUrl = (input.apiUrl ?? DEFAULT_API_URL).replace(/\/+$/, '');
  if (apiUrl !== DEFAULT_API_URL && input.anonKey === undefined) {
    throw new VibecadeError(
      VibecadeErrorCode.InvalidConfig,
      'config.anonKey is required when overriding apiUrl (the baked-in key only matches the production platform)',
    );
  }
  return {
    gameId: input.gameId,
    apiKey: input.apiKey,
    apiUrl,
    anonKey: input.anonKey ?? DEFAULT_ANON_KEY,
    debug: input.debug ?? false,
    autoHostMessages: input.autoHostMessages ?? true,
    // Defaulting to gameId keeps every existing consumer on exactly the
    // storage key it already uses, so upgrading the SDK cannot sign anyone
    // out.
    authScope: input.authScope ?? input.gameId,
  };
}
